import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchOptions, fetchFood } from '../api/api';
import { ReactComponent as LogoPoney } from '../assets/images/logoPoney.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCustomNavigation } from './NavigationContext';
import {
  faFacebookF,
  faInstagram,
  faTwitter,
} from '@fortawesome/free-brands-svg-icons';

import styles from '../styles/components/_Navigation.module.scss';
const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [isInverted, setIsInverted] = useState(false);
  const [socialLinks, setSocialLinks] = useState([]);
  const navigationRef = useRef(null);
  const location = useLocation();
  const { customNavigate } = useCustomNavigation();

  const getBrightness = (element) => {
    if (!element) return 0;
    const bgColor = window.getComputedStyle(element).backgroundColor;
    const rgb = bgColor.match(/\d+/g);
    if (!rgb) return 0;
    return (parseInt(rgb[0]) * 299 + parseInt(rgb[1]) * 587 + parseInt(rgb[2]) * 114) / 1000;
  };

  useEffect(() => {
    const updateNavClass = () => {
      if (navigationRef.current) {
        const brightness = getBrightness(navigationRef.current);
        setIsInverted(brightness > 128);
      }
    };

    const observer = new MutationObserver(updateNavClass);
    observer.observe(document.body, { 
      attributes: true,
      childList: true,
      subtree: true 
    });

    window.addEventListener('scroll', updateNavClass);
    window.addEventListener('resize', updateNavClass);
    updateNavClass();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', updateNavClass);
      window.removeEventListener('resize', updateNavClass);
    };
  }, []);

  const { data: options } = useQuery({
    queryKey: ['options'],
    queryFn: fetchOptions,
  });

  const { data: foods = [] } = useQuery({
    queryKey: ['foods'],
    queryFn: fetchFood,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (options?.a_propos?.reseaux_sociaux) {
      setSocialLinks(options.a_propos.reseaux_sociaux);
    }
  
  }, [options]);

  useEffect(() => {
    const handleScroll = () => setHasScrolled(window.scrollY > 0);
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMenuClick = (path) => {
    customNavigate(path);
    setIsMenuOpen(false);
  };

  const menuItems = [
    { path: '/', label: 'Accueil', className: 'nav-home' },
    { path: '/programmation', label: 'programmation', className: 'nav-agenda' }, 
    ...(foods.length > 0 ? [{ path: '/food', label: 'Food & Drinks', className: 'nav-food' }] : []),
    { path: '/billetterie', label: 'Billetterie', className: 'nav-tickets' }
  ];

  const leftMenuItems = menuItems.slice(0, 3);
  const rightMenuItems = menuItems.slice(3);

  const menuVariants = {
    closed: {
      opacity: 0,
      y: '-100%',
      transition: { duration: 0.3, ease: 'easeInOut' }
    },
    open: {
      opacity: 1,
      y: '0%',
      transition: { duration: 0.3, ease: 'easeInOut' }
    }
  };

  const menuItemVariants = {
    closed: { opacity: 0, y: 20 },
    open: { opacity: 1, y: 0 }
  };

  return (
    <div
      className={`${styles.navigationContainer} ${
        hasScrolled ? styles.scrolled : ''
      } ${isInverted ? styles.inverted : ''}`}
      ref={navigationRef}
    >
      <nav className={styles.navigation}>
        <div className={styles.leftMenu}>
          <ul className={styles.menu}>
            {leftMenuItems.map((item) => (
              <li key={item.path} className={`${styles.menuItem} ${styles[item.className]}`}>
                <a
                  href={item.path}
                  className={location.pathname === item.path ? styles.active : ''}
                  onClick={(e) => {
                    e.preventDefault();
                    handleMenuClick(item.path);
                  }}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className={styles.logo}>
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              handleMenuClick('/');
            }}
          >
            <LogoPoney className={styles.logoSvg} />
          </a>
        </div>

        <div className={styles.menuWrapper}>
          <ul className={styles.menu}>
            {rightMenuItems.map((item) => (
              <li key={item.path} className={`${styles.menuItem} ${styles[item.className]}`}>
                <a
                  href={item.path}
                  className={location.pathname === item.path ? styles.active : ''}
                  onClick={(e) => {
                    e.preventDefault();
                    handleMenuClick(item.path);
                  }}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
          <div className={styles.socialLinks}>
            {socialLinks.map((link) => {
              const icon =
                link.nom.toLowerCase() === 'facebook'
                  ? faFacebookF
                  : link.nom.toLowerCase() === 'instagram'
                  ? faInstagram
                  : link.nom.toLowerCase() === 'twitter'
                  ? faTwitter
                  : null;

              return (
                icon && (
                  <a
                    key={link.nom}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={link.nom}
                  >
                    <FontAwesomeIcon icon={icon} />
                  </a>
                )
              );
            })}
          </div>
        </div>

        <button
          className={`${styles.menuToggle} ${isMenuOpen ? styles.active : ''}`}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className={styles.bar}></span>
          <span className={styles.bar}></span>
          <span className={styles.bar}></span>
        </button>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              className={styles.mobileMenu}
              initial="closed"
              animate="open"
              exit="closed"
              variants={menuVariants}
            >
              <motion.ul className={styles.mobileMenuList}>
                {menuItems.map((item) => (
                  <motion.li
                    key={item.path}
                    variants={menuItemVariants}
                    className={styles.mobileMenuItem}
                  >
                    <a
                      href={item.path}
                      className={location.pathname === item.path ? styles.active : ''}
                      onClick={(e) => {
                        e.preventDefault();
                        handleMenuClick(item.path);
                      }}
                    >
                      {item.label}
                    </a>
                  </motion.li>
                ))}
              </motion.ul>
              <motion.div 
                className={styles.mobileSocialLinks}
                variants={menuItemVariants}
              >
                {socialLinks.map((link) => {
                  const icon =
                    link.nom.toLowerCase() === 'facebook'
                      ? faFacebookF
                      : link.nom.toLowerCase() === 'instagram'
                      ? faInstagram
                      : link.nom.toLowerCase() === 'twitter'
                      ? faTwitter
                      : null;

                  return (
                    icon && (
                      <a
                        key={link.nom}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={link.nom}
                      >
                        <FontAwesomeIcon icon={icon} />
                      </a>
                    )
                  );
                })}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </div>
  );
};

export default Navigation;